import { Card, CardHeader } from '../../../../components/ui/Card';
import { useTopicHeatmap } from '../../../../hooks/useVisibility';
import { useFilters } from '../filters';
import { PROVIDER_LABELS } from '../format';
import { RAMP_INDIGO } from '../../../../components/ui/chart';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

/**
 * Visibility is a magnitude, so the scale is one hue getting stronger — a
 * red-to-green ladder would imply low visibility is an error rather than just low.
 * Empty cells stay neutral so "no data" never reads as a low score.
 */
function heatmapColor(v: number): string | undefined {
  if (v <= 0) return undefined; // neutral surface via class
  if (v < 5) return RAMP_INDIGO[5];
  if (v < 15) return RAMP_INDIGO[4];
  if (v < 30) return RAMP_INDIGO[3];
  if (v < 50) return RAMP_INDIGO[2];
  return RAMP_INDIGO[0];
}

export function ClustersPage() {
  const { startDate, endDate } = useFilters();
  const { data, isLoading } = useTopicHeatmap(startDate, endDate);

  return (
    <Card padding="md">
      <CardHeader
        title="Topic clusters"
        description="Own-brand visibility by topic and engine — find where you're invisible."
      />
      {isLoading ? (
        <LoadingState />
      ) : !data || data.cells.length === 0 ? (
        <EmptyState compact title="No topic-cluster data yet — assign topicClusterId to probes (or wait one sweep) to populate." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm border-separate" style={{ borderSpacing: 2 }}>
            <thead>
              <tr>
                <th className="text-left py-1 px-2 font-medium text-body-xs text-text-tertiary"></th>
                {data.engines.map((e) => (
                  <th key={e.id} className="text-center py-1 px-2 font-medium text-body-xs text-text-tertiary" title={e.description}>
                    {PROVIDER_LABELS[e.id.split('-')[0] ?? ''] ?? e.id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.clusters.map((cluster) => (
                <tr key={cluster.id ?? 'none'}>
                  <th className="text-left py-1 px-2 font-medium text-body-xs whitespace-nowrap">{cluster.name}</th>
                  {data.engines.map((e) => {
                    const cell = data.cells.find((c) => (c.clusterId ?? 'none') === (cluster.id ?? 'none') && c.engineId === e.id);
                    const v = cell?.visibility ?? 0;
                    return (
                      <td
                        key={e.id}
                        className={`text-center py-2 px-3 rounded tabular-nums text-body-xs ${
                          v <= 0 ? 'bg-surface-secondary' : ''
                        } ${v >= 50 ? 'text-white' : 'text-text-secondary'}`}
                        style={{ backgroundColor: heatmapColor(v), minWidth: 70 }}
                        title={cell ? `${cell.visibilityCount}/${cell.visibilityTotal} chats cited` : 'No data'}
                      >
                        {cell ? `${v.toFixed(0)}%` : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
